import { useMemo } from 'react';
import { CannCompoundingSurveyData, ValidationError, FormValidationResult } from '../types/survey.types';

export const useSurveyValidation = () => {
  const validateField = (field: keyof CannCompoundingSurveyData, value: any): ValidationError | null => {
    switch (field) {
      case 'patientInitials':
        if (!value || typeof value !== 'string') {
          return { field, message: 'Patient initials are required' };
        }
        if (value.length > 3) {
          return { field, message: 'Patient initials must be 3 characters or less' };
        }
        if (!/^[A-Za-z]+$/.test(value)) {
          return { field, message: 'Patient initials must contain only letters' };
        }
        return null;

      case 'age':
        if (!value || isNaN(Number(value))) {
          return { field, message: 'Age is required and must be a number' };
        }
        const age = Number(value);
        if (age < 18 || age > 120) {
          return { field, message: 'Age must be between 18 and 120' };
        }
        return null;

      case 'gender':
        if (!value) {
          return { field, message: 'Gender selection is required' };
        }
        const validGenders = ['Male', 'Female', 'Non-binary', 'Prefer not to say'];
        if (!validGenders.includes(value)) {
          return { field, message: 'Please select a valid gender option' };
        }
        return null;

      case 'productName':
        if (!value || typeof value !== 'string') {
          return { field, message: 'Product name is required' };
        }
        if (value.trim().length < 2) {
          return { field, message: 'Product name must be at least 2 characters long' };
        }
        return null;

      case 'clinicalConditions':
        if (!value || !Array.isArray(value) || value.length === 0) {
          return { field, message: 'At least one clinical condition must be selected' };
        }
        return null;

      case 'otherCondition':
        // Only validate if "Other" is selected in clinical conditions
        if (value && typeof value === 'string' && value.trim().length < 2) {
          return { field, message: 'Please specify the other condition (at least 2 characters)' };
        }
        return null;

      case 'weeksOfUse':
        if (!value || isNaN(Number(value))) {
          return { field, message: 'Duration of use is required and must be a number' };
        }
        const weeks = Number(value);
        if (weeks < 3) {
          return { field, message: 'Survey can only be completed after at least 3 weeks of use' };
        }
        if (weeks > 260) { // 5 years
          return { field, message: 'Duration seems unusually long. Please verify.' };
        }
        return null;

      case 'previousCannabisUse':
        if (typeof value !== 'boolean') {
          return { field, message: 'Please indicate previous cannabis use' };
        }
        return null;

      case 'previousProducts':
        // Only required if previousCannabisUse is true
        // This validation would be handled in the form component
        return null;

      case 'satisfactionRating':
        if (!value || isNaN(Number(value))) {
          return { field, message: 'Satisfaction rating is required' };
        }
        const rating = Number(value);
        if (rating < 1 || rating > 5) {
          return { field, message: 'Satisfaction rating must be between 1 and 5' };
        }
        return null;

      case 'sideEffects':
      case 'additionalFeedback':
        // These are optional fields, no validation needed
        return null;

      default:
        return null;
    }
  };

  const validateForm = (data: Partial<CannCompoundingSurveyData>): FormValidationResult => {
    const errors: ValidationError[] = [];

    // Required fields validation
    const requiredFields: (keyof CannCompoundingSurveyData)[] = [
      'patientInitials',
      'age',
      'gender',
      'productName',
      'clinicalConditions',
      'weeksOfUse',
      'previousCannabisUse',
      'satisfactionRating'
    ];

    for (const field of requiredFields) {
      const error = validateField(field, data[field]);
      if (error) {
        errors.push(error);
      }
    }

    // Special validations

    // If "Other" is selected in clinical conditions, otherCondition is required
    if (data.clinicalConditions?.includes('Other')) {
      if (!data.otherCondition || data.otherCondition.trim().length < 2) {
        errors.push({
          field: 'otherCondition',
          message: 'Please specify the other condition'
        });
      }
    }

    // If previous cannabis use is true, previous products should be provided
    if (data.previousCannabisUse === true) {
      if (!data.previousProducts || data.previousProducts.trim().length < 2) {
        errors.push({
          field: 'previousProducts',
          message: 'Please specify previous cannabis products used'
        });
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  const getFieldError = (errors: ValidationError[], field: string): string | undefined => {
    const error = errors.find(e => e.field === field);
    return error?.message;
  };

  return useMemo(() => ({
    validateField,
    validateForm,
    getFieldError
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);
};

export default useSurveyValidation;